import { CustomStyle, ImageMattingSdk, ThemeType, type MattingOptions } from '@meitu/image-matting';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { convertImageToOrigin } from '@meitu/saas-editor-core/es/utils/file-kit';
import { type MattingInfo } from '@meitu/x-design-ai-api';
import { getLocalIdentity } from '@meitu/xdesign-identity';
import { getLocaleStore } from '@/i18n';
import { isIntl } from '@/react-process-env';
import { getAppKey } from '@/utils/app-key';
import { getServerEnv } from '@/utils/env';

export const AI_MATTING_LIMIT_SIZE = 2496;

type MattingCompletedCallback = (resultBase64: string, mattingInfo: MattingInfo) => void;

type InitOptions = {
    anchorEl: HTMLElement;
    theme?: ThemeType;
    onload?: () => void;
    onCancel?: () => void;
    onDownloadableUpdate?: (data: boolean) => void;
    onMattingStatusUpdate?: (data: string) => void;
    customStyle?: CustomStyle;
};

// SDK instance
let instance: ImageMattingSdk | null = null;

// complete callback
let onMattingCompleted: MattingCompletedCallback;

// tryMatting buffer
// prevent `tryMatting` called before sdk loaded
type TryMattingBuffer = MattingOptions | null;
let tryMattingBuffer: TryMattingBuffer = null;
let xMtccClient = '';

export const initMattingSDK = async (options: InitOptions) => {
    const { locale } = getLocaleStore();

    if (instance) return;

    if (!options.anchorEl) {
        throw new Error('anchorEl type is HTMLElement, but got null');
    }

    instance = new ImageMattingSdk();

    await instance.init({
        isIntl,
        parentDom: options.anchorEl,
        iframeType: isIntl ? 'editor-plugin-intl' : 'editor-plugin',
        env: getServerEnv(),
        appKey: getAppKey(),
        mtstatCommonParams: {
            oid: getLocalIdentity()?.oid || '',
        },
        locale,
        theme: options.theme,
        customStyle: options.customStyle,

        onCancel() {
            options.onCancel?.();
        },

        onMattingCompleted: ({ imageURL: resultBase64, mattingInfo }) => {
            if (!resultBase64 || !mattingInfo) {
                return;
            }

            if (onMattingCompleted) {
                onMattingCompleted(resultBase64, mattingInfo);
            }
        },

        onDownloadableUpdate: (data) => {
            options.onDownloadableUpdate?.(data);
        },

        onMattingStatusUpdate: (data) => {
            options.onMattingStatusUpdate?.(data);
        },
    });

    // await updateMTCCClient();

    options.onload?.();

    // use buffer data to matting image
    if (tryMattingBuffer) {
        instance.tryMatting(tryMattingBuffer);
        tryMattingBuffer = null;
    }
};

export const tryMatting = async (imageInfo: MattingOptions['imageInfo'], callback: MattingCompletedCallback) => {
    onMattingCompleted = callback;

    const url = await base64Kit.getRemoteUrlAsync(imageInfo.url);
    imageInfo.url = convertImageToOrigin(url);

    if (!instance) {
        tryMattingBuffer = { imageInfo };
        return;
    }
    return instance.tryMatting({
        imageInfo,
        extraInfo: { isUseNewAPI: true },
    });
};

export const extractResult = async () => {
    if (!instance) return;

    return instance.extract();
};

export const destroyMattingSDK = () => {
    if (instance) {
        instance.destroy();
        instance = null;
    }
};

// export const updateMTCCClient = async () => {
//     try {
//         xMtccClient =
//             (await genMtccHeader({
//                 key: 'plugin-matting',
//             })) ?? '';
//     } catch (error) {
//         console.log(error);
//     }

//     if (!instance) return;
//     instance?.setMTCCClient(xMtccClient);
// };

export const updateTheme = (theme: ThemeType) => {
    if (!instance) return;
    instance?.setTheme(theme);
};

export const updateCustomStyle = (customStyle: CustomStyle) => {
    if (!instance) return;
    instance?.setCustomStyle(customStyle);
};
